import { OnMutateFn } from '@diritto/common/base/mutateFunction';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodePublicRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { ReplyPublicRelayDto, ReplyRelayDto } from '@diritto/common/relay/dtos/episode-reply-relay.dto';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { ellipsis } from '@diritto/common/utils/string';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ProfileImage } from 'app-components/accounts/profiles/ProfileImage';
import { ReplyReaction } from 'app-components/reply/ReplyReaction';
import { EMPTY_PROFILE_TEXT } from 'constants/profile';
import { ReplyEditAction } from 'constants/reply';
import { useAuthState } from 'contexts/AuthProvider';
import { ArrowRightSlimIcon } from 'icons/ArrowRightSlimIcon';
import { AuthorBadgeIcon } from 'icons/AuthorBadgeIcon';
import { ReReplyIcon } from 'icons/ReReplyIcon';
import { usePathname, useSearchParams } from 'next/navigation';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { LinkIt, urlRegex } from 'react-linkify-it';
import { getTextLineStyle } from 'styles/common';
import { Divider } from 'ui-components/common/Divider';
import { ExternalLink } from 'ui-components/common/ExternalLink';
import { Link } from 'ui-components/common/Link';
import { RelativeDateText } from 'ui-components/common/RelativeDateText';
import { useIsInViewport } from 'ui-hooks/useIsInViewport';

interface ReplyProps {
  className?: string;
  reply: ReplyPublicRelayDto;
  profile: ProfileLitePublicRelayDto;
  episode?: EpisodePublicRelayDto;
  content?: ContentPublicRelayDto;
  authorProfile?: ProfileLitePublicRelayDto;
  parentReplyId?: number;

  onDispatchAction: (action: ReplyEditAction, reply: ReplyPublicRelayDto, onMutate?: OnMutateFn) => void;
  onClickReply?: (reply: ReplyRelayDto) => void;
  onClickReReply?: (reply: ReplyRelayDto) => void;

  isFocused: boolean;
  isLiked: boolean;
  isAuthorReply: boolean;
  myReplyEditable: boolean;
  myReplyDeletable: boolean;
  showMenuButton: boolean;
  showReReplyWrite: boolean;
  showReReplyCount: boolean;
  showMyReplyStyle: boolean;
  showContentInfo: boolean;
  showPinned: boolean;
}

const makeExternalLink = (match: string, key: number) => (
  <ExternalLink href={match} key={key}>
    <StyledLink>{match}</StyledLink>
  </ExternalLink>
);

export const Reply = memo(
  ({
    className,
    reply,
    profile,
    episode,
    content,
    authorProfile,
    parentReplyId,

    onDispatchAction,
    onClickReply,
    onClickReReply,

    isFocused,
    isLiked,
    isAuthorReply,

    myReplyEditable,
    myReplyDeletable,
    showMenuButton,
    showReReplyWrite,
    showReReplyCount,
    showMyReplyStyle,
    showContentInfo,
    showPinned,
  }: ReplyProps) => {
    const { profile: myProfile } = useAuthState();
    const isMyReply = reply.profileId === myProfile?.id;

    const ref = useRef<HTMLDivElement>(null);
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const isInViewport = useIsInViewport(ref, isFocused);

    const createQueryString = useCallback(
      (params: Record<string, string>) => {
        const newSearchParams = new URLSearchParams((searchParams ?? '').toString());
        Object.entries(params).forEach(([name, value]) => {
          newSearchParams.set(name, value);
        });

        return newSearchParams.toString();
      },
      [searchParams],
    );

    const isContentAuthor = useMemo(() => authorProfile?.id === myProfile?.id, [authorProfile?.id, myProfile?.id]);

    const handleClickReply = useCallback(() => onClickReply?.(reply), [onClickReply, reply]);

    const actions = useMemo(() => {
      const deleteActionBy = isContentAuthor ? ReplyEditAction.DeleteByAuthor : ReplyEditAction.DeleteByUser;
      if (isMyReply && myReplyEditable && myReplyDeletable) {
        return [ReplyEditAction.ActiveEdit, deleteActionBy];
      }
      if (isMyReply && myReplyEditable) {
        return [ReplyEditAction.ActiveEdit];
      }
      if (isMyReply && myReplyDeletable) {
        return [deleteActionBy];
      }

      if (isMyReply) {
        return [];
      }

      if (isContentAuthor) {
        return [ReplyEditAction.DeleteByAuthor, ReplyEditAction.Report];
      }

      return [ReplyEditAction.Report];
    }, [isContentAuthor, isMyReply, myReplyDeletable, myReplyEditable]);

    useEffect(() => {
      if (isFocused && ref?.current) {
        ref?.current?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
      }
      return () => {};
    }, [ref, isFocused]);

    useEffect(() => {
      if (isFocused && isInViewport) {
        window.history.replaceState({}, '', `${pathname}?${createQueryString({ focusedReplyId: String(reply.id) })}`);
        // router.replace(`${pathname}?${createQueryString({ focusedReplyId: String(reply.id) })}`);
      }
    }, [isFocused, isInViewport, reply.id, pathname, createQueryString]);

    return (
      <StyledContainer
        id={String(reply.id)}
        ref={ref}
        className={className}
        isMyReply={showMyReplyStyle && isMyReply}
        isClickable={!!onClickReply}
        onClick={handleClickReply}>
        {parentReplyId && (
          <StyledIconWrap>
            <ReReplyIcon />
          </StyledIconWrap>
        )}
        <StyledProfile>
          {profile ? (
            <Link href={`/accounts/profiles/${profile.id}`}>
              <ProfileImage file={profile?.profileImage} defaultImageKey={profile?.nickname} size={32} />
            </Link>
          ) : (
            <ProfileImage size={32} />
          )}
        </StyledProfile>
        <StyledReplyInfo>
          {showPinned && reply.isPinned && <StyledPinned>📌 {authorProfile?.nickname}님이 고정함</StyledPinned>}
          {reply.scheduledDeletedAt && <StyledScheduledDeleted>삭제 예정</StyledScheduledDeleted>}
          <StyledTop>
            <StyledName>
              {profile ? <Link href={`/accounts/profiles/${profile.id}`}>{profile.nickname}</Link> : EMPTY_PROFILE_TEXT}
              {isAuthorReply && <AuthorBadgeIcon />}
            </StyledName>
            <StyledDate date={reply.createdAt} />
          </StyledTop>
          <StyledDescription>
            <LinkIt component={makeExternalLink} regex={urlRegex}>
              {reply.description}
            </LinkIt>
          </StyledDescription>
          {((showContentInfo && content) || episode) && (
            <StyledLinkWrapper>
              {showContentInfo && content && (
                <>
                  <Link href={`/contents/${content.id}`}>
                    <StyledEpisodeTitle>{ellipsis(content.title, 15)}</StyledEpisodeTitle>
                  </Link>
                  <StyledArrowRightSlimIconWrapper>
                    <ArrowRightSlimIcon size={12} color={Color.Gray300} />
                  </StyledArrowRightSlimIconWrapper>
                </>
              )}
              {episode && (
                <Link href={`/contents/${episode.contentId}/episodes/${episode.id}`}>
                  <StyledEpisodeTitle>{ellipsis(episode.title, 15)}</StyledEpisodeTitle>
                </Link>
              )}
            </StyledLinkWrapper>
          )}
          <ReplyReaction
            reply={reply}
            actions={actions}
            isLiked={isLiked}
            authorProfile={authorProfile}
            onDispatchAction={onDispatchAction}
            showMenuButton={showMenuButton}
            showReReplyWrite={showReReplyWrite}
            showReReplyCount={showReReplyCount}
            showLike
            onClickReReply={onClickReReply}
          />
        </StyledReplyInfo>
        <StyledDivider position="bottom" color={Color.Gray200} />
      </StyledContainer>
    );
  },
);

Reply.displayName = 'Reply';

const StyledArrowRightSlimIconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0 2px;
`;

const StyledLinkWrapper = styled.div`
  display: flex;
  margin-top: 4px;
  flex-direction: row;
  align-items: center;
  height: 16px;
`;

const StyledContainer = styled.div(
  ({ isMyReply, isClickable }: { isMyReply: boolean; isClickable: boolean }) => css`
    display: flex;
    background-color: ${isMyReply ? Color.Gray100 : Color.White};
    position: relative;
    padding: 16px;
    width: 100%;
    cursor: ${isClickable ? 'pointer' : 'default'};
  `,
);

const StyledLink = styled.span`
  color: ${Color.Blue500};
  text-decoration: underline;
  word-break: break-all;
  white-space: break-spaces;
  word-wrap: anywhere;
`;

const StyledIconWrap = styled.div`
  display: flex;
  margin-right: 8px;
`;

const StyledDivider = styled(Divider)`
  width: calc(100% - 32px);
  left: 16px;
`;

const StyledProfile = styled.div`
  flex-shrink: 0;
`;

const StyledReplyInfo = styled.div`
  width: calc(100% - 32px - 16px - 16px);
  margin-left: 16px;
  position: relative;
  flex-grow: 1;
`;

const StyledTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledName = styled.div`
  display: flex;
  align-items: center;
  ${Typography.XXSmall};

  & > a {
    margin-right: 4px;
    width: fit-content;
    font-weight: ${FontWeight.Bold};
    ${getTextLineStyle(1)};
  }

  & > p {
    margin-right: 4px;
    width: fit-content;
    font-weight: ${FontWeight.Bold};
    ${getTextLineStyle(1)};
  }
`;

const StyledDate = styled(RelativeDateText)`
  width: fit-content;
  margin-left: 10px;
  font-weight: ${FontWeight.Regular};
  color: ${Color.Gray500};
  ${Typography.XXXSmall};
  ${getTextLineStyle(1)};
`;

const StyledEpisodeTitle = styled.p`
  font-weight: ${FontWeight.Medium};
  color: ${Color.Gray500};
  ${Typography.XXXSmall};
  word-break: break-all;
  white-space: break-spaces;
`;

const StyledDescription = styled.p`
  margin-top: 4px;
  font-weight: ${FontWeight.Regular};
  color: ${Color.Gray900};
  ${Typography.XXSmall}
  word-break: break-all;
  white-space: break-spaces;
`;

const StyledPinned = styled.div`
  font-weight: ${FontWeight.Medium};
  color: ${Color.Gray600};
  ${Typography.XXXSmall};
  margin-bottom: 4px;
`;

const StyledScheduledDeleted = styled.div`
  display: flex;
  padding: 1px 4px;
  justify-content: center;
  align-items: center;
  width: fit-content;

  border-radius: 100px;
  background: ${Color.Red100};

  font-weight: ${FontWeight.Medium};
  color: ${Color.Red500};
  ${Typography.XXXSmall};
  text-align: center;

  margin-bottom: 4px;
`;
